import type { PlanState, TaskEvent, TaskMessage, TaskState } from '../../task-runtime/types'

/**
 * The deliberately small vocabulary consumed by the future Codex-style renderer.
 * It contains user-visible intent and outcomes, never transport/debug metadata.
 */
export type ActivityPresentationItem =
  | { id: string; sequence: number; kind: 'working'; text: string }
  | { id: string; sequence: number; kind: 'message'; role: 'user' | 'notice'; text: string; sourceId: string }
  | { id: string; sequence: number; kind: 'decision'; text: string; sourceId: string; streaming?: boolean }
  | { id: string; sequence: number; kind: 'action'; actionType: 'plan' | 'search' | 'command' | 'file' | 'test' | 'tool' | 'review' | 'image' | 'browser'; text: string; detail?: string; status?: string; sourceId: string }
  | { id: string; sequence: number; kind: 'approval'; text: string; detail?: string; sourceId: string }
  | { id: string; sequence: number; kind: 'warning' | 'error'; text: string; sourceId: string }
  | { id: string; sequence: number; kind: 'completion'; text: string; sourceId: string; status: 'completed' | 'failed' | 'cancelled' | 'interrupted' }

export type ActivityPresentationInput = {
  state: TaskState
  plan?: PlanState
  fallbackMessages?: TaskMessage[]
}

export type ActivityTiming = {
  active: boolean
  startedAt?: number
  endedAt?: number
  elapsedMs: number
}

export type ActivityChange = {
  path: string
  additions: number
  deletions: number
  sourceId: string
}

const RAW_KEYS = new Set(['rawReasoning', 'raw_reasoning', 'reasoningContent', 'reasoningText', 'textDelta'])
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'interrupted'])
const ROUTINE_KINDS = new Set(['usage', 'compaction', 'diagnostic', 'reroute', 'delta'])
const ACTION_KINDS = new Set(['search', 'command', 'diff', 'tool', 'review', 'image', 'browser'])
const MAX_TEXT_LENGTH = 480

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
}

function text(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function safeText(value: unknown, depth = 0): string {
  if (depth > 2 || value == null) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) {
    return value
      .slice(0, 8)
      .map((entry) => safeText(entry, depth + 1))
      .filter(Boolean)
      .join(' ')
  }
  const record = asRecord(value)
  if (!record) return ''
  for (const key of ['summary', 'message', 'text', 'delta', 'output', 'command', 'query', 'toolName', 'serverName', 'path', 'title', 'name', 'reason', 'status']) {
    if (RAW_KEYS.has(key)) continue
    const candidate = safeText(record[key], depth + 1)
    if (candidate) return candidate
  }
  return ''
}

function bounded(value: string) {
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH)}…` : value
}

function itemId(event: TaskEvent) {
  const item = asRecord(event.payload.item)
  return event.itemId ?? (typeof item?.id === 'string' ? item.id : undefined)
}

function messageSequence(message: TaskMessage, events: TaskEvent[], fallback: number) {
  return events.find((event) => itemId(event) === message.id)?.sequence ?? fallback
}

function eventLabel(event: TaskEvent) {
  const item = asRecord(event.payload.item)
  const type = text(item?.type)
  if (event.kind === 'search' || event.method?.includes('webSearch') || type === 'webSearch') return 'Search'
  if (event.kind === 'command' || event.method?.includes('commandExecution') || type === 'commandExecution') return 'Command'
  if (event.kind === 'diff' || event.method?.includes('fileChange') || type === 'fileChange') return 'File change'
  if (event.kind === 'tool' || event.method?.includes('mcp') || type === 'mcpToolCall') return 'Tool'
  if (event.kind === 'review') return 'Review'
  if (event.kind === 'image') return 'Image'
  if (event.kind === 'browser') return 'Browser'
  return 'Activity'
}

function actionType(event: TaskEvent): Extract<ActivityPresentationItem, { kind: 'action' }>['actionType'] | null {
  const item = asRecord(event.payload.item)
  const itemType = text(item?.type)
  if (event.kind === 'search' || event.method?.includes('webSearch')) return 'search'
  if (itemType === 'webSearch') return 'search'
  if (event.kind === 'command' || event.method?.includes('commandExecution') || itemType === 'commandExecution') return 'command'
  if (event.kind === 'diff' || event.method?.includes('fileChange') || itemType === 'fileChange') return 'file'
  if (event.kind === 'tool' || event.method?.includes('mcp') || ['mcpToolCall', 'dynamicToolCall', 'collabToolCall'].includes(itemType)) return 'tool'
  if (event.kind === 'review') return 'review'
  if (event.kind === 'image') return 'image'
  if (event.kind === 'browser') return 'browser'
  return ACTION_KINDS.has(event.kind) ? event.kind as Extract<ActivityPresentationItem, { kind: 'action' }>['actionType'] : null
}

function cleanStatusText(value: string) {
  return value.replace(/^\*+\s*|\s*\*+$/g, '').trim()
}

function currentActivityText(state: TaskState) {
  const events = [...state.events].sort((left, right) => right.sequence - left.sequence)
  for (const event of events) {
    if (event.method === 'turn/completed') continue
    const item = asRecord(event.payload.item)
    const itemType = text(item?.type)
    const content = eventContent(event)
    if (event.kind === 'reasoning' && content) return cleanStatusText(content)
    const type = actionType(event)
    if (type === 'search') return content && content !== 'Search' ? `Searching ${content}` : 'Searching the project'
    if (type === 'command') return content && content !== 'Command' ? (/^Ran\s/i.test(actionText(event, type, content)) ? actionText(event, type, content).replace(/^Ran\s/i, 'Running ') : `Running ${content}`) : 'Inspecting project files'
    if (type === 'tool') return content && content !== 'Tool' ? `Using ${content}` : 'Inspecting project context'
    if (type === 'file') return itemType === 'fileChange' ? 'Reviewing file changes' : 'Inspecting relevant files'
    if (type === 'browser') return 'Inspecting the browser'
    if (type === 'review') return 'Reviewing the result'
    if (event.method === 'turn/started') return 'Inspecting the project'
  }
  return state.status === 'queued' ? 'Preparing the task' : 'Thinking through the task'
}

function eventDetail(event: TaskEvent) {
  const item = asRecord(event.payload.item)
  return text(event.payload.path) || text(item?.path) || text(event.payload.cwd) || undefined
}

function eventStatus(event: TaskEvent) {
  const item = asRecord(event.payload.item)
  const value = text(event.payload.status) || text(item?.status) || text(event.method?.split('/').at(-1))
  if (!value || /^(completed|complete|success|succeeded)$/i.test(value)) return undefined
  if (/^(inProgress|in_progress|started)$/i.test(value)) return 'in progress'
  return value
}

function eventContent(event: TaskEvent) {
  const item = asRecord(event.payload.item)
  const value = event.kind === 'reasoning'
    ? safeText(event.payload.summary) || safeText(event.payload.title) || safeText(event.payload.delta) || safeText(event.payload.text)
    : safeText(event.payload) || safeText(item)
  return bounded(value)
}

function actionText(event: TaskEvent, type: Extract<ActivityPresentationItem, { kind: 'action' }>['actionType'], content: string) {
  if (!content) return content
  const method = event.method ?? ''
  if (type === 'command') return /output|delta/i.test(method) ? `Output: ${content}` : `Ran ${content}`
  if (type === 'file') return /^updated\s/i.test(content) ? content : `Updated ${content}`
  if (type === 'search') return /^searched\s/i.test(content) ? content : `Searched ${content}`
  if (type === 'tool') return /^used\s/i.test(content) ? content : `Used ${content}`
  if (type === 'review') return /^reviewed\s/i.test(content) ? content : `Reviewed ${content}`
  if (type === 'browser') return /^opened\s/i.test(content) ? content : `Opened ${content}`
  return content
}

function numberValue(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : undefined
}

function diffLineStats(value: unknown) {
  if (typeof value !== 'string') return { additions: 0, deletions: 0 }
  let additions = 0
  let deletions = 0
  for (const line of value.split(/\r?\n/u)) {
    if (line.startsWith('+++') || line.startsWith('---')) continue
    if (line.startsWith('+')) additions += 1
    if (line.startsWith('-')) deletions += 1
  }
  return { additions, deletions }
}

function changeFromEvent(event: TaskEvent): ActivityChange | null {
  const payload = event.payload
  const item = asRecord(payload.item)
  const path = text(payload.path) || text(payload.file) || text(item?.path) || text(item?.file)
  if (!path) return null
  const stats = asRecord(payload.stats) ?? asRecord(item?.stats)
  const lineStats = diffLineStats(payload.diff ?? item?.diff)
  return {
    path: bounded(path),
    additions: numberValue(payload.additions) ?? numberValue(payload.linesAdded) ?? numberValue(stats?.additions) ?? lineStats.additions,
    deletions: numberValue(payload.deletions) ?? numberValue(payload.linesRemoved) ?? numberValue(stats?.deletions) ?? lineStats.deletions,
    sourceId: event.eventId,
  }
}

function isMessageEvent(event: TaskEvent) {
  const item = asRecord(event.payload.item)
  const type = text(item?.type)
  return type === 'userMessage' || type === 'agentMessage' || event.method === 'item/agentMessage/delta'
}

function isTerminal(state: TaskState) {
  return TERMINAL_STATUSES.has(state.status)
}

function isActive(state: TaskState) {
  return state.running || state.status === 'queued' || state.status === 'running'
}

function terminalStatus(state: TaskState): Extract<ActivityPresentationItem, { kind: 'completion' }>['status'] {
  if (state.status === 'failed' || state.status === 'cancelled' || state.status === 'interrupted') return state.status
  return 'completed'
}

function dedupe(items: ActivityPresentationItem[]) {
  const seen = new Set<string>()
  return items.filter((item) => {
    // Keep each lifecycle item once, even if an app-server replay repeats it.
    // Event identity is authoritative: two identical commands can be valid
    // separate actions and must not disappear from the activity stream.
    const key = item.kind === 'working' ? 'working' : `${item.kind}:${item.sourceId}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function isSupervisorPlanPayload(value: string) {
  const first = value.indexOf('{')
  const last = value.lastIndexOf('}')
  if (first < 0 || last <= first) return false
  try {
    const parsed = JSON.parse(value.slice(first, last + 1)) as { steps?: unknown; plan?: { steps?: unknown } }
    return Array.isArray(parsed.steps) || Array.isArray(parsed.plan?.steps)
  } catch {
    return false
  }
}

/**
 * Derives elapsed task time from authoritative lifecycle events. The renderer
 * can pass a changing `now` value while active without mutating the task.
 */
export function selectActivityTiming(state: TaskState, now = Date.now()): ActivityTiming {
  const starts = state.events
    .filter((event) => event.method === 'turn/started')
    .map((event) => event.timestamp)
    .filter((timestamp) => Number.isFinite(timestamp))
  const ends = state.events
    .filter((event) => event.method === 'turn/completed')
    .map((event) => event.timestamp)
    .filter((timestamp) => Number.isFinite(timestamp))
  // A task can contain several turns. The visible timer belongs to the
  // current/latest turn, not to the lifetime of the thread.
  const startedAt = starts.length ? Math.max(...starts) : undefined
  const endedAt = ends.length ? Math.max(...ends) : undefined
  const active = isActive(state)
  const elapsedMs = startedAt === undefined
    ? 0
    : Math.max(0, (active ? now : endedAt ?? now) - startedAt)
  return { active, startedAt, endedAt, elapsedMs }
}

/** Returns the latest bounded diff snapshot for each changed file. */
export function selectActivityChanges(state: TaskState): ActivityChange[] {
  const latest = new Map<string, ActivityChange>()
  for (const event of [...state.events].sort((left, right) => left.sequence - right.sequence)) {
    if (event.kind !== 'diff' && !event.method?.includes('fileChange')) continue
    const change = changeFromEvent(event)
    if (change) latest.set(change.path, change)
  }
  return [...latest.values()].slice(0, 40)
}

/**
 * Converts canonical task state to the intentionally small activity vocabulary.
 * This function is pure: it does not mutate state, persist memory, or execute tools.
 */
export function selectActivityPresentation({ state, plan, fallbackMessages = [] }: ActivityPresentationInput): ActivityPresentationItem[] {
  const knownMessageIds = new Set(state.messages.map((message) => message.id))
  const messages = [...state.messages, ...fallbackMessages.filter((message) => !knownMessageIds.has(message.id))]
  const terminal = isTerminal(state)
  const output: ActivityPresentationItem[] = []

  messages.forEach((message, index) => {
    const content = bounded(text(message.content))
    if (!content) return
    // The workflow planner's machine-readable JSON is represented by the
    // structured plan card. Rendering that payload as assistant prose creates
    // a noisy duplicate and leaks implementation protocol into the transcript.
    if (message.role === 'assistant' && plan && isSupervisorPlanPayload(text(message.content))) return
    const sequence = messageSequence(message, state.events, state.events.length + index + 1)
    if (message.role === 'user' || message.role === 'notice') {
      output.push({ id: `message-${message.id}`, sequence, kind: 'message', role: message.role, text: content, sourceId: message.id })
      return
    }
    if (terminal) {
      output.push({ id: `completion-${message.id}`, sequence, kind: 'completion', text: content, sourceId: message.id, status: terminalStatus(state) })
    } else {
      output.push({ id: `decision-${message.id}`, sequence, kind: 'decision', text: content, sourceId: message.id, streaming: message.streaming })
    }
  })

  if (plan?.activeStepId) {
    const step = plan.steps.find((candidate) => candidate.id === plan.activeStepId)
    if (step) output.push({ id: `plan-${step.id}`, sequence: Number.MAX_SAFE_INTEGER - 1, kind: 'action', actionType: 'plan', text: bounded(step.title), detail: 'Plan step', status: text(step.status), sourceId: `plan:${step.id}` })
  }

  const lifecycleActions = new Map<string, Extract<ActivityPresentationItem, { kind: 'action' }>>()
  state.events.forEach((event) => {
    if (isMessageEvent(event) || ROUTINE_KINDS.has(event.kind)) return
    const sourceId = event.eventId
    const content = eventContent(event)
    if (event.kind === 'reasoning') {
      if (content) output.push({ id: `decision-${sourceId}`, sequence: event.sequence, kind: 'decision', text: content, sourceId })
      return
    }
    if (event.kind === 'approval' || event.method?.includes('requestApproval')) {
      output.push({ id: `approval-${sourceId}`, sequence: event.sequence, kind: 'approval', text: content || eventLabel(event), detail: eventDetail(event), sourceId })
      return
    }
    if (event.kind === 'warning') {
      output.push({ id: `warning-${sourceId}`, sequence: event.sequence, kind: 'warning', text: content || 'Warning', sourceId })
      return
    }
    if (event.kind === 'error') {
      output.push({ id: `error-${sourceId}`, sequence: event.sequence, kind: 'error', text: content || 'Task error', sourceId })
      return
    }
    const type = actionType(event)
    if (type) {
      const fallback = eventLabel(event)
      const lifecycleId = itemId(event)
      const previous = lifecycleId ? lifecycleActions.get(lifecycleId) : undefined
      const action: Extract<ActivityPresentationItem, { kind: 'action' }> = {
        id: lifecycleId ? `action-item-${lifecycleId}` : `action-${sourceId}`,
        sequence: previous?.sequence ?? event.sequence,
        kind: 'action',
        actionType: type,
        text: actionText(event, type, content || fallback),
        detail: eventDetail(event),
        status: eventStatus(event),
        sourceId: lifecycleId ? `item:${lifecycleId}` : sourceId,
      }
      if (lifecycleId) lifecycleActions.set(lifecycleId, action)
      else output.push(action)
    }
  })
  output.push(...lifecycleActions.values())

  const visible = dedupe(output).sort((left, right) => left.sequence - right.sequence || left.id.localeCompare(right.id))
  if (isActive(state)) visible.push({ id: 'working', sequence: Number.MAX_SAFE_INTEGER, kind: 'working', text: currentActivityText(state) })
  return visible
}
